import React from 'react';

import { AVATAR_SIZE } from '@depop/web-ui-kit/Avatar';

import { BagSoldOutItems } from '../BagSoldOutItems';

import styles from './styles.module.css';

import { BagProduct } from '@/components/Bag/BagProduct';
import { BagProductPlaceholder } from '@/components/Bag/BagProductPlaceholder';
import { QuickAccessBagPricingAndCheckout } from '@/components/Bag/QuickAccessBagPricingAndCheckout';
import { ExtendedLink } from '@/components/ExtendedLink';
import { UserDetails } from '@/components/UserDetails';
import { UserDetailsPlaceholder } from '@/components/UserDetails/UserDetailsPlaceholder';
import { type DetailedBagBanners } from '@/modules/bag/aliases';
import { getNonoptimisticProducts } from '@/modules/bag/helpers';
import {
  type BagDetailedSellerCache,
  isOptimisticProduct,
  isOptimisticSeller,
} from '@/modules/bag/types';
import { type ProductNudges } from '@/modules/nudges/aliases';
import {
  filterRequiredPictures,
  getNearestSizePictureFormat,
} from '@/modules/pictures/helpers';
import { isSold } from '@/modules/product/helpers/isSold';

type Props = {
  seller: BagDetailedSellerCache['seller'];
  products: BagDetailedSellerCache['products'];
  summary: BagDetailedSellerCache['summary'];
  banners?: DetailedBagBanners;
  showAcceptedOfferPrice?: boolean;
  nudgesData: ProductNudges;
} & React.ComponentPropsWithRef<'div'>;

function QuickAccessBagSeller({
  seller,
  products,
  summary,
  nudgesData,
  banners,
  ...rest
}: Props) {
  const sellerPictureUrl =
    seller.picture &&
    getNearestSizePictureFormat(filterRequiredPictures(seller.picture), 32);

  const unsoldProducts = products.filter(
    (product) => !isSold(product.status || '')
  );
  const soldProducts = products.filter((product) =>
    isSold(product.status || '')
  );
  const nonoptimisticSoldProducts = getNonoptimisticProducts(soldProducts);

  return (
    <div {...rest}>
      {isOptimisticSeller(seller) ? (
        <UserDetailsPlaceholder />
      ) : (
        <ExtendedLink
          href={`/${seller.username}`}
          className={styles.userDetailsAnchor}
        >
          <UserDetails
            pictureUrl={sellerPictureUrl?.url}
            firstName={seller.first_name}
            lastName={seller.last_name}
            username={seller.username}
            verified={seller.verified}
            avatarSize={AVATAR_SIZE.EXTRA_SMALL}
          />
        </ExtendedLink>
      )}
      <div className={styles.wrapper}>
        {unsoldProducts.length > 0 && (
          <>
            <div className={styles.products}>
              {unsoldProducts.map((product) => {
                if (isOptimisticProduct(product)) {
                  return (
                    <BagProductPlaceholder
                      key={`${product.id}-${product.variant_id}`}
                    />
                  );
                }
                return (
                  <BagProduct
                    key={`${product.id}-${product.variant_id}`}
                    product={product}
                    sellerId={seller.id}
                    showInBagNudge
                    nudges={nudgesData[product.id] || []}
                  />
                );
              })}
            </div>
            <QuickAccessBagPricingAndCheckout
              summary={summary}
              banners={banners}
              unsoldProducts={unsoldProducts}
              seller={seller}
            />
          </>
        )}
        {soldProducts.length > 0 && (
          <div className={styles.soldOutWrapper}>
            <BagSoldOutItems
              products={nonoptimisticSoldProducts}
              sellerId={seller.id}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export { QuickAccessBagSeller };
